import React, {useCallback, useEffect, useState} from "react";
import { Modal,Tag,Row,Col,Switch,message,Table, InputNumber,Select } from "antd";
import { DeleteTwoTone,UpCircleTwoTone,DownCircleTwoTone,MenuOutlined } from "@ant-design/icons"
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import _ from "lodash";
import useRequest from "@/utils/useRequest";
import Input from "./input";
import "./component.css";

const DraggableRow = ({ children, ...props }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props['data-row-key'],
  });

  const style = {
    ...props.style,
    transform: CSS.Transform.toString(transform && {
      ...transform,
      scaleY: 1,
    }),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  return (
    <tr {...props} ref={setNodeRef} style={style} {...attributes}>
      {React.Children.map(children, (child, index) => {
        // 第一列是拖拽句柄列
        if (index === 0) {
          return React.cloneElement(child, {
            children: (
              <div {...listeners} style={{ cursor: 'grab', color: '#999' }}>
                <MenuOutlined />
              </div>
            ),
          });
        }
        return child;
      })}
    </tr>
  );
};

const UseEditColumns = (props) => {
    const { topic,colsettings, list } = props;
    const { request } = useRequest();
    
    const [visible,setVisible] = useState(false);

    const show = () => {
        setVisible(true);
    }
    const close = () => {
        setVisible(false);
    }
    const submit = async (settings,onFinish) => {
        message.loading("保存配置中...",0);
        const save = async () => {
            let result = await request("/api/v2/private/columns/save","POST","json",{key:topic,settings:settings});
            if(result.success){
                close();
                onFinish(settings);
            }
        }
        console.log('save settings', settings);
        await save();
        message.destroy();
    }

    const WscEditColumnsModal = ({ key,onFinish }) => {
        // 把比较函数抽象处理，用于下边的find和findIndex
        // @param item 数组中当前比较的元素
        // @param key  要查找的当前在编辑的列的名称：可能是字符串，可能是数组
        const cmpKeyWithItemInArray = useCallback(key => item => {
            // 如果字段本身是数组
            if (Array.isArray(key)) {
                return Array.isArray(item.dataIndex) && key.length === item.dataIndex.length &&
                    key.every((val, index) => val === item.dataIndex[index]);
            }
            return item.dataIndex == key;
        }, []);
        // 获取当前在修改的列
        // @param settings 所有列的配置信息
        // @param key      当前编辑的列的名称：可能是字符串，可能是数组
        const getChangedTarget = useCallback((settings, key)=> {
            return settings.find(cmpKeyWithItemInArray(key));
        }, []);
        const getTargetIndex = useCallback((settings, key) => settings.findIndex(cmpKeyWithItemInArray(key)), []);

        // 通过对象中的key名称和字段的值，构造出真正的字段名称
        // 1 如果值本身就是简单类型或者数组，则字段名称就是key
        // 2 如果值本身是一个object，且object只有一个key，则递归获取key值，并组合成一个数组作为字段名称
        //   例如： {current: {$numberDecimal: 10.0 }} => ['current', '$numberDecimal']
        const getValueKeys = useCallback(value => {
            const keys = !!value ? Object.keys(value) : [];
            if (typeof value === "object" && value !== null && !Array.isArray(value) && keys.length === 1) {
                return [keys[0], ...getValueKeys(value[keys[0]])];
            }
            return [];
        }, []);
        const getRealKeys = useCallback((key, value)=> {
            const valueKeys = getValueKeys(value);
            if (valueKeys.length === 0) return key;
            return [key, ...valueKeys];
        }, []);

        //获取list中的对象属性,合并colsettings提供编辑
        const mergeSettings = () => {
            let targetSettings = [];
            // 这里的合并要考虑，数组列的问题，attr是一个object，而不是简单的字符串或数字
            if(list?.length > 0){
                for(let attr in list[0])
                {
                    const realKey = getRealKeys(attr, list[0][attr]);
                    // let index = colsettings?.findIndex(item=>item.dataIndex == attr);
                    const index = getTargetIndex(colsettings, realKey);
                    if(index == -1){
                        targetSettings.push({title:attr,dataIndex:attr,visible:false});
                    }
                }
            }
            return targetSettings.concat(_.cloneDeep(colsettings));
        }
        const [settings, setSettings] = useState([]);

        useEffect(()=>{
            const tmp = mergeSettings(colsettings);
            setSettings(tmp);
        },[colsettings,list]);

        const onChange = (key,field,value) => {
            const _settings = _.cloneDeep(settings);
            const targetObj = getChangedTarget(_settings, key);
            if(targetObj) {
                targetObj[field] = value;
            }
            setSettings(_settings);
        }

        const onChangeWidth = (key,field,value) => {
            let _settings = _.cloneDeep(settings);
            const targetObj = getChangedTarget(_settings, key);
            if(targetObj && value != 0) {
                targetObj[field] = value;
                setSettings(_settings);
            }
        }

        const onChangeResponsive = (key, field, value) => {
            console.log('onChangeResponsive', key, field, value);
            const _settings = _.cloneDeep(settings);
            const targetObj = getChangedTarget(_settings, key);
            if (targetObj) {
                if (value.length > 0) {
                    targetObj[field] = value;
                } else {
                    delete targetObj[field];
                }
                setSettings(_settings);
            }
        };

        const onRemove = (key) => {
            let _settings = _.cloneDeep(settings);
            let index = getTargetIndex(_settings, key);
            if (index > -1) {
                _settings.splice(index, 1);
                setSettings(_settings);
            }
        }

        const DragHandle = () => (
            <MenuOutlined
              style={{
                cursor: 'grab',
                color: '#999',
              }}
            />
          );

        const columns = [
            {
              title: '顺序',
              dataIndex: 'sort',
              width: 60,
              render: () => <DragHandle />,
            }, 
            {
              title: '字段',
              dataIndex: 'dataIndex',
              render: (val) => {
                  if (Array.isArray(val)) {
                      return (
                          <>
                              {val.map(item => <Tag key={item}>{item}</Tag>)}
                          </>
                      );
                  }
                  return <Tag>{val}</Tag>
              },
            },
            {
              title: '显示名称',
              dataIndex: 'title',
              width: 240,
              render: (val,item) => <Input size="small" value={val} onChange={e=>onChange(item.dataIndex,"title",e)} />,
            }, {
              title: '显示',
              dataIndex: 'visible',
              width: 80,
              render: (fileld,record) => <Switch size="small"  checked={record.visible} onChange={val=>onChange(record.dataIndex,"visible",val)} />,
            },
            {
              title: '宽度',
              dataIndex: 'width',

              render: (fileld,record) => <InputNumber size="small" value={record.width?record.width:0} onChange={val=>onChangeWidth(record.dataIndex,"width",val)} />,
            },
            {
                title: '响应式',
                dataIndex: 'responsive',
                render(value, record){
                    return (
                        <Select
                            style={{width: '80px'}}
                            size='small'
                            mode='multiple'
                            value={value ? value : undefined}
                            placeholder='按需配置'
                            options={[
                                {value: 'xxl', label: 'xxl'},
                                {value: 'xl', label: 'xl'},
                                {value: 'lg', label: 'lg'},
                                {value: 'md', label: 'md'},
                                {value: 'sm', label: 'sm'},
                                {value: 'xs', label: 'xs'},
                            ]}
                            onChange={value => {
                                onChangeResponsive(record.dataIndex,"responsive",value);
                            }}
                        />
                    )
                }
            },
            {
              title: '排序',
              dataIndex: 'sorter',
              width: 80,
              render: (fileld,record) => <Switch size="small"  checked={record.sorter} onChange={val=>onChange(record.dataIndex,"sorter",val)} />
            },
            {
                title: '删除',
                dataIndex: '*',
                width: 60,
                render: (fileld,record) => <DeleteTwoTone onClick={()=>onRemove(record.dataIndex)} />
            }
          ];

          const handleDragEnd = (event) => {
            const { active, over } = event;

            if (active.id !== over?.id) {
              const oldIndex = settings.findIndex((item) => {
                const key = Array.isArray(item.dataIndex) ? item.dataIndex.join('/') : item.dataIndex;
                return key === active.id;
              });
              const newIndex = settings.findIndex((item) => {
                const key = Array.isArray(item.dataIndex) ? item.dataIndex.join('/') : item.dataIndex;
                return key === over.id;
              });

              setSettings((items) => arrayMove(items, oldIndex, newIndex));
            }
          };

          const sensors = useSensors(
            useSensor(PointerSensor),
            useSensor(KeyboardSensor, {
              coordinateGetter: sortableKeyboardCoordinates,
            })
          );

          const DraggableContainer = (props) => (
            <tbody {...props} />
          );
          
          const DraggableBodyRow = (props) => {
            return <DraggableRow {...props} />;
          };

        const rowKeys = settings.map(record => {
            if (Array.isArray(record.dataIndex)) return record.dataIndex.join('/');
            return record.dataIndex;
        });

        return (<Modal title="配置表头" 
                    width={960}
                    open={visible} 
                    onOk={()=>{
                        submit(settings,onFinish);
                    }}
                    onCancel={close}>
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                            modifiers={[restrictToVerticalAxis]}
                        >
                            <SortableContext
                                items={rowKeys}
                                strategy={verticalListSortingStrategy}
                            >
                                <Table
                                    size="small"
                                    pagination={false}
                                    dataSource={settings}
                                    columns={columns}
                                    rowKey={record => {
                                        if (Array.isArray(record.dataIndex)) return record.dataIndex.join('/');
                                        return record.dataIndex;
                                    }}
                                    components={{
                                        body: {
                                            wrapper: DraggableContainer,
                                            row: DraggableBodyRow,
                                        },
                                    }}
                                />
                            </SortableContext>
                        </DndContext>
                        
            </Modal>);
    }
    return { show, close, WscEditColumnsModal };
}

export default UseEditColumns;